import React, { useEffect, useState } from 'react';
import walletService from '../services/walletService';
import { fetchUsersWithProfiles } from '../services/userService';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../shared/providers/LanguageProvider';
import { useNavigate } from 'react-router-dom';
import { ShimmerTable, ShimmerBox } from '../components/common/Shimmer';
import AddMoneyModal from '../components/AddMoneyModal';
import { PageHeader } from '@/components/ui/page-header';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const Wallets = () => {
  const { token } = useAuth();
  const { t } = useLanguage(); // eslint-disable-line no-unused-vars
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ user_id: '', initial_balance: 0 });
  const [formError, setFormError] = useState(null);
  const [users, setUsers] = useState([]);
  const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);

  useEffect(() => {
    setLoading(true);
    walletService
      .getWallets(token)
      .then(setWallets)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    fetchUsersWithProfiles(token)
      .then((usersList) => setUsers(usersList || []))
      .catch((err) => console.error('Error fetching users:', err));
  }, [token]);

  const getUserDetails = (userId) => {
    return users.find((u) => u.id === userId);
  };

  const filteredWallets = wallets.filter((w) => {
    const user = getUserDetails(w.user_id);
    const userName =
      user?.full_name ||
      user?.user_metadata?.full_name ||
      user?.raw_user_meta_data?.full_name ||
      user?.name ||
      '';
    return (
      w.user_id?.toLowerCase().includes(search.toLowerCase()) ||
      w.status?.toLowerCase().includes(search.toLowerCase()) ||
      user?.email?.toLowerCase().includes(search.toLowerCase()) ||
      userName.toLowerCase().includes(search.toLowerCase())
    );
  });

  const openModal = () => {
    setForm({ user_id: '', initial_balance: 0 });
    setFormError(null);
    setShowModal(true);
  };

  const handleQuickStatusToggle = async (wallet) => {
    const newStatus = wallet.status === 'active' ? 'suspended' : 'active';
    const suspensionReason = newStatus === 'suspended' ? 'Quick suspend from admin panel' : null;

    try {
      await walletService.updateWallet(token, wallet.id, {
        status: newStatus,
        suspension_reason: suspensionReason,
      });
      setLoading(true);
      walletService
        .getWallets(token)
        .then(setWallets)
        .catch(setError)
        .finally(() => setLoading(false));
    } catch (err) {
      setError(err);
    }
  };

  const handleAddMoney = (wallet) => {
    setSelectedWallet(wallet);
    setShowAddMoneyModal(true);
  };

  const handleAddMoneySuccess = () => {
    setLoading(true);
    walletService
      .getWallets(token)
      .then(setWallets)
      .catch(setError)
      .finally(() => setLoading(false));
  };

  const getUserName = (user) =>
    user?.full_name ||
    user?.user_metadata?.full_name ||
    user?.raw_user_meta_data?.full_name ||
    user?.name ||
    user?.email?.split('@')[0] ||
    '-';

  const handleCreateWallet = async (e) => {
    e.preventDefault();
    setFormError(null);
    try {
      if (!form.user_id) {
        setFormError('User is required');
        return;
      }

      const balance = parseFloat(form.initial_balance);
      if (isNaN(balance) || balance < 0) {
        setFormError('Initial balance must be a valid number greater than or equal to 0');
        return;
      }

      await walletService.createWallet(token, form.user_id, balance);
      setShowModal(false);
      setForm({ user_id: '', initial_balance: 0 });
      setLoading(true);
      walletService
        .getWallets(token)
        .then(setWallets)
        .catch(setError)
        .finally(() => setLoading(false));
    } catch (err) {
      let errorMessage = 'Failed to create wallet. Please try again.';

      if (err.response) {
        const errorData = err.response.data;
        if (errorData.details && Array.isArray(errorData.details)) {
          errorMessage = errorData.details.map((d) => d.msg || d.message).join(', ');
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else {
          errorMessage = `Request failed with status code ${err.response.status}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setFormError(errorMessage);
      console.error('Error creating wallet:', {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
        user_id: form.user_id,
        initial_balance: form.initial_balance,
      });
    }
  };

  return (
    <div className="px-2 sm:px-0">
      <PageHeader
        title="Wallets"
        actions={
          <Button onClick={openModal} className="w-full sm:w-auto">
            Create Wallet
          </Button>
        }
      />
      <Input
        type="text"
        placeholder="Search by name, email, or status..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-4 w-full sm:max-w-md"
      />
      {loading ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <ShimmerBox width="w-64" height="h-10" />
            <ShimmerBox width="w-32" height="h-10" />
          </div>
          <ShimmerTable rows={8} columns={9} />
        </div>
      ) : error ? (
        <div className="text-destructive">{error.message || 'Error loading wallets'}</div>
      ) : (
        <>
          <Card className="hidden lg:block">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Wallet ID</TableHead>
                    <TableHead>User Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWallets.map((wallet) => {
                    const user = getUserDetails(wallet.user_id);
                    return (
                      <TableRow key={wallet.id}>
                        <TableCell className="font-mono text-xs">
                          {wallet.id.substring(0, 8)}...
                        </TableCell>
                        <TableCell>{getUserName(user)}</TableCell>
                        <TableCell>{user?.email || '-'}</TableCell>
                        <TableCell>
                          {user?.phone || user?.user_metadata?.phone || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={wallet.status === 'active' ? 'success' : 'destructive'}
                          >
                            {wallet.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(wallet.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => navigate(`/wallets/${wallet.user_id}`)}
                            >
                              Details
                            </Button>
                            <Button size="sm" onClick={() => handleAddMoney(wallet)}>
                              Add $
                            </Button>
                            <Button
                              size="sm"
                              variant={wallet.status === 'active' ? 'destructive' : 'default'}
                              onClick={() => handleQuickStatusToggle(wallet)}
                            >
                              {wallet.status === 'active' ? 'Suspend' : 'Activate'}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="space-y-4 lg:hidden">
            {filteredWallets.map((wallet) => {
              const user = getUserDetails(wallet.user_id);
              return (
                <Card key={wallet.id}>
                  <CardContent className="p-4">
                    <div className="mb-3 flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-foreground">{getUserName(user)}</h3>
                        <p className="text-sm text-muted-foreground">{user?.email || '-'}</p>
                      </div>
                      <Badge variant={wallet.status === 'active' ? 'success' : 'destructive'}>
                        {wallet.status}
                      </Badge>
                    </div>

                    <div className="mb-3 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Phone:</span>
                        <span>{user?.phone || user?.user_metadata?.phone || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Wallet ID:</span>
                        <span className="font-mono text-xs">{wallet.id.substring(0, 8)}...</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Created:</span>
                        <span>{new Date(wallet.created_at).toLocaleDateString()}</span>
                      </div>
                      {wallet.suspended_at && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Suspended:</span>
                            <span>{new Date(wallet.suspended_at).toLocaleDateString()}</span>
                          </div>
                          {wallet.suspension_reason && (
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Reason:</span>
                              <span className="text-end">{wallet.suspension_reason}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    <div className="flex flex-col space-y-2">
                      <Button
                        className="w-full"
                        variant="secondary"
                        onClick={() => navigate(`/wallets/${wallet.user_id}`)}
                      >
                        View Details
                      </Button>
                      <div className="grid grid-cols-2 gap-2">
                        <Button onClick={() => handleAddMoney(wallet)}>Add Money</Button>
                        <Button
                          variant={wallet.status === 'active' ? 'destructive' : 'default'}
                          onClick={() => handleQuickStatusToggle(wallet)}
                        >
                          {wallet.status === 'active' ? 'Suspend' : 'Activate'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Wallet</DialogTitle>
          </DialogHeader>
          {formError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {formError}
            </div>
          )}
          <form onSubmit={handleCreateWallet} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="user_id">User</Label>
              <Select
                value={form.user_id || undefined}
                onValueChange={(value) => {
                  setForm((f) => ({ ...f, user_id: value }));
                  setFormError(null);
                }}
                required
              >
                <SelectTrigger id="user_id">
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => {
                    const hasWallet = wallets.some((w) => w.user_id === u.id);
                    return (
                      <SelectItem key={u.id} value={u.id}>
                        {u.email} {hasWallet ? '(has wallet)' : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {users.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400">Loading users...</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="initial_balance">Initial Balance</Label>
              <Input
                id="initial_balance"
                name="initial_balance"
                type="number"
                step="0.01"
                value={form.initial_balance}
                onChange={(e) => setForm((f) => ({ ...f, initial_balance: e.target.value }))}
                min="0"
                placeholder="0.00"
                required
              />
              <p className="text-xs text-muted-foreground">
                Enter 0 for a new wallet with no initial balance
              </p>
            </div>
            <DialogFooter>
              <Button type="submit" className="w-full">
                Create Wallet
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AddMoneyModal
        isOpen={showAddMoneyModal}
        onClose={() => setShowAddMoneyModal(false)}
        wallet={selectedWallet}
        onSuccess={handleAddMoneySuccess}
      />
    </div>
  );
};

export default Wallets;
